import { useEffect, useMemo, useState } from "react";
import { Plus, Building2, User, Users, Phone, Mail, MapPin, Trash2, FolderOpen, Bell, Clock, Search, ArrowUpDown, ArrowUp, ArrowDown, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { eur, pct, margeText } from "../../lib/format";
import { useViewRole } from "../../context/ViewRoleContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import ActionLogDialog, { ActionTimeline } from "../actions/ActionLogDialog";
import ProjectDetailSheet from "../projects/ProjectDetailSheet";

const TYPES = ["Particulier", "Professionnel", "Apporteur d'affaires", "Promoteur", "Syndic", "Entreprise"];
const TYPE_ICON = { Particulier: User, Professionnel: Building2, Entreprise: Building2, Promoteur: Building2, Syndic: Users };

function Stat({ label, value, color, testId }) {
  return (
    <div className="rounded-lg bg-[#111827] border border-[#1E293B] p-3">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className={`font-mono-metric text-base font-bold ${color}`} data-testid={testId}>{value}</div>
    </div>
  );
}

export default function ClientsView() {
  const { canSeeMargins, isAdmin } = useViewRole();
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null); // 360 payload
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    nom: "",
    type: "Particulier",
    ville: "",
    email: "",
    telephone: "",
    contacts: [],
    familles_interet: [],
  });
  const [actionOpen, setActionOpen] = useState(false);
  const [actionCtx, setActionCtx] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [projectOpen, setProjectOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [familles, setFamilles] = useState([]);

  useEffect(() => {
    api.get("/familles")
      .then((r) => setFamilles(r.data || []))
      .catch(() => setFamilles([]));
  }, []);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("Tous");
  const [sortKey, setSortKey] = useState("nom");
  const [sortDir, setSortDir] = useState("asc");

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openClient = (c) => {
    setSelected(c);
    setDetail(null);
    api.get(`/clients/${c.id}/360`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  };

  useEffect(() => {
    const handleGlobalClientOpen = (event) => {
      const clientId = event.detail?.clientId;
      if (!clientId) return;

      const client = clients.find((c) => c.id === clientId);
      if (client) {
        openClient(client);
      }
    };

    window.addEventListener("crm:open-client", handleGlobalClientOpen);
    return () => window.removeEventListener("crm:open-client", handleGlobalClientOpen);
  }, [clients]);

  const refreshDetail = () => {
    if (selected) api.get(`/clients/${selected.id}/360`).then((r) => setDetail(r.data)).catch(() => {});
    setRefreshKey((k) => k + 1);
    load();
  };

  const create = async () => {
    if (!form.nom) return toast.error("Nom requis");
    try {
      await api.post("/clients", { ...form, contacts: form.contacts.filter((c) => c.nom) });
      toast.success("Client ajouté");
      setOpen(false);
      setForm({
      nom: "",
      type: "Particulier",
      ville: "",
      email: "",
      telephone: "",
      contacts: [],
      familles_interet: [],
    });
      load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/clients/${id}`); toast.success("Client supprimé"); setSelected(null); load(); }
    catch { toast.error("Suppression réservée à l'admin"); }
  };

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...clients]
      .filter((c) => (!q || [c.nom, c.ville, c.email, c.telephone, c.type].filter(Boolean).join(" ").toLowerCase().includes(q)))
      .filter((c) => typeFilter === "Tous" || c.type === typeFilter)
      .sort((a, b) => {
        let av, bv;
        if (sortKey === "ca") {
          av = Number(a.ca_cumule_ht || 0); bv = Number(b.ca_cumule_ht || 0);
        } else if (sortKey === "devis") {
          av = Number(a.nb_devis || 0); bv = Number(b.nb_devis || 0);
        } else {
          av = (a.nom || "").toLowerCase(); bv = (b.nom || "").toLowerCase();
        }
        const cmp = typeof av === "string" ? av.localeCompare(bv, "fr") : av - bv;
        return sortDir === "asc" ? cmp : -cmp;
      });
  }, [clients, search, typeFilter, sortKey, sortDir]);

  const setSort = (key) => {
    if (sortKey === key) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir(key === "nom" ? "asc" : "desc");
    }
  };

  const clientTypes = useMemo(() => ["Tous", ...Array.from(new Set(clients.map((c) => c.type).filter(Boolean))).sort((a, b) => a.localeCompare(b, "fr"))], [clients]);

  const logAction = (ctx) => { setActionCtx(ctx); setActionOpen(true); };
  const openProject = (pid) => { setProjectId(pid); setProjectOpen(true); };

  return (
    <div className="space-y-6" data-testid="clients-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Clients & Contacts</h1>
          <p className="text-slate-400 text-sm mt-1">Vision 360° : historique, projets, actions et relances.</p>
        </div>
        <Button data-testid="add-client-button" onClick={() => setOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">
          <Plus className="h-4 w-4 mr-1" /> Nouveau client
        </Button>
      </div>

      <div className="rounded-xl bg-[#111827] border border-[#1E293B] p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input data-testid="clients-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un client, ville, téléphone, email..."
              className="w-full h-10 rounded-lg bg-[#0B0F17] border border-[#1E293B] pl-9 pr-9 text-sm outline-none focus:border-amber-500/60" />
            {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"><X className="h-4 w-4" /></button>}
          </div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-10 rounded-lg bg-[#0B0F17] border border-[#1E293B] px-3 text-sm text-slate-300">
            {clientTypes.map((t) => <option key={t} value={t}>{t === "Tous" ? "Tous les types" : t}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-2 text-slate-500"><ArrowUpDown className="h-3.5 w-3.5" /> Trier :</div>
          <div className="flex gap-2">
            <button onClick={() => setSort("nom")} className="px-2.5 py-1.5 rounded-md border border-[#1E293B] text-slate-400">Nom {sortKey === "nom" && (sortDir === "asc" ? "A–Z" : "Z–A")}</button>
            <button onClick={() => setSort("ca")} className="px-2.5 py-1.5 rounded-md border border-[#1E293B] text-slate-400">CA {sortKey === "ca" && (sortDir === "asc" ? "↑" : "↓")}</button>
            <button onClick={() => setSort("devis")} className="px-2.5 py-1.5 rounded-md border border-[#1E293B] text-slate-400">Devis {sortKey === "devis" && (sortDir === "asc" ? "↑" : "↓")}</button>
          </div>
          <span className="text-slate-500">{filteredClients.length} client{filteredClients.length > 1 ? "s" : ""}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredClients.map((c) => {
          const Icon = TYPE_ICON[c.type] || Building2;
          return (
            <button
              key={c.id}
              data-testid={`client-card-${c.id}`}
              onClick={() => openClient(c)}
              className="text-left rounded-xl bg-[#111827] border border-[#1E293B] p-5 hover:border-amber-500/30 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-[#0B0F17] border border-[#1E293B] flex items-center justify-center">
                    <Icon className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <div className="font-semibold">{c.nom}</div>
                    <div className="text-xs text-slate-500">{c.type}{c.ville ? ` · ${c.ville}` : ""}</div>
                  </div>
                </div>
                <span className="text-[11px] px-2 py-0.5 rounded bg-slate-700/40 text-slate-300">{c.nb_devis} devis</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-[#1E293B]">
                <div>
                  <div className="text-[11px] text-slate-500">CA signé</div>
                  <div className="font-mono-metric text-sm font-semibold text-sky-400">{eur(c.ca_cumule_ht)}</div>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500">Marge moy.</div>
                  <div className={`font-mono-metric text-sm font-semibold ${canSeeMargins ? margeText(c.taux_marge_moyen_pct) : "text-slate-600"}`}>
                    {canSeeMargins ? pct(c.taux_marge_moyen_pct) : "•••"}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* ---------------- Fiche client 360 ---------------- */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="bg-[#0B0F17] border-[#1E293B] text-slate-100 w-full sm:max-w-xl overflow-y-auto" data-testid="client-360-sheet">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-head text-xl text-slate-100">{selected.nom}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-5">
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-[11px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">{selected.type}</span>
                  {selected.ville && <span className="inline-flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3.5 w-3.5" /> {selected.ville}</span>}
                  {selected.telephone && <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Phone className="h-3.5 w-3.5" /> {selected.telephone}</span>}
                  {selected.email && <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Mail className="h-3.5 w-3.5" /> {selected.email}</span>}
                </div>

                <div>
                  <h3 className="text-sm font-semibold mb-2">Familles d’intérêt</h3>
                  {selected.familles_interet?.length ? (
                    <div className="flex flex-wrap gap-2">
                      {selected.familles_interet.map((famille) => (
                        <span
                          key={famille}
                          className="rounded-full px-3 py-1.5 text-xs font-medium bg-blue-500/10 border border-blue-400/30 text-blue-300"
                        >
                          {famille}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">Aucune famille renseignée.</p>
                  )}
                </div>

                {!detail ? (
                  <div className="text-sm text-slate-500">Chargement de la fiche…</div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <Stat label="CA signé" value={eur(detail.stats.ca_signe_ht)} color="text-emerald-400" testId="c360-ca-signe" />
                      <Stat label="CA potentiel" value={eur(detail.stats.ca_potentiel_ht)} color="text-sky-400" testId="c360-ca-potentiel" />
                      <Stat label="CA facturé" value={eur(detail.stats.ca_facture_ht)} color="text-amber-400" testId="c360-ca-facture" />
                      <Stat label="Marge signée" value={canSeeMargins ? eur(detail.stats.marge_signee_ht) : "•••"} color="text-emerald-400" testId="c360-marge" />
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
                      <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Dernière activité : <span className="text-slate-200">{detail.stats.derniere_activite || "—"}</span></span>
                      <span className="inline-flex items-center gap-1.5"><Bell className="h-3.5 w-3.5 text-amber-400" /> Prochaine action : <span className="text-slate-200">{detail.stats.prochaine_action || "—"}</span></span>
                    </div>

                    <Button
                      data-testid="c360-log-action"
                      onClick={() => logAction({ client_id: selected.id, client_nom: selected.nom })}
                      className="w-full bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Enregistrer une action
                    </Button>

                    {detail.projets.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><FolderOpen className="h-4 w-4 text-slate-400" /> Projets ({detail.projets.length})</h3>
                        <div className="space-y-2">
                          {detail.projets.map((p) => (
                            <button key={p.id} data-testid={`c360-projet-${p.id}`} onClick={() => openProject(p.id)}
                              className="w-full text-left flex items-center justify-between rounded-lg bg-[#111827] border border-[#1E293B] p-3 hover:border-amber-500/40">
                              <div>
                                <div className="text-sm font-medium">{p.titre || p.famille}</div>
                                <div className="text-xs text-slate-500">{p.statut}{p.famille ? ` · ${p.famille}` : ""}</div>
                              </div>
                              <FolderOpen className="h-4 w-4 text-slate-500" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><Bell className="h-4 w-4 text-slate-400" /> Historique des actions</h3>
                      <ActionTimeline actions={detail.actions} />
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold mb-2">Familles achetées</h3>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(detail.devis.reduce((acc, d) => {
                          const f = d.famille || (d.lignes || []).find((l) => l.famille)?.famille || "Autre";
                          acc[f] = (acc[f] || 0) + 1;
                          return acc;
                        }, {})).sort((a, b) => b[1] - a[1]).map(([f, n]) => (
                          <span key={f} className="px-2.5 py-1.5 rounded-md bg-[#111827] border border-[#1E293B] text-xs text-slate-300">{f} · {n}</span>
                        ))}
                        {detail.devis.length === 0 && <span className="text-xs text-slate-500">Aucune famille enregistrée.</span>}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold mb-2">Dossiers & devis ({detail.devis.length})</h3>
                      <div className="space-y-2">
                        {detail.devis.map((d) => (
                          <div key={d.id} className="flex items-center justify-between rounded-lg bg-[#111827] border border-[#1E293B] p-3">
                            <div>
                              <div className="text-sm font-medium">{d.famille || d.titre}</div>
                              <div className="text-xs text-slate-500 font-mono-metric">{d.numero} · {d.statut}{d.type_vente ? ` · ${d.type_vente}` : ""}</div>
                            </div>
                            <span className="font-mono-metric text-sm font-semibold">{eur(d.montant_total ?? d.total_ht)}</span>
                          </div>
                        ))}
                        {detail.devis.length === 0 && <p className="text-xs text-slate-500">Aucun dossier.</p>}
                      </div>
                    </div>
                  </>
                )}

                {isAdmin && (
                  <Button variant="outline" data-testid="client-delete-button" onClick={() => remove(selected.id)} className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10">
                    <Trash2 className="h-4 w-4 mr-1" /> Supprimer ce client
                  </Button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* ---------------- Nouveau client ---------------- */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111827] border-[#1E293B] text-slate-100">
          <DialogHeader><DialogTitle className="font-head">Nouveau client</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Nom / Raison sociale</Label>
              <Input data-testid="client-input-nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="bg-[#0B0F17] border-[#1E293B]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="bg-[#0B0F17] border-[#1E293B]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="text-slate-400 text-xs">Familles d’intérêt</Label>

              <div className="flex flex-wrap gap-2">
                {familles.map((famille) => {
                  const selected = form.familles_interet?.includes(famille.nom);

                  return (
                    <button
                      key={famille.id}
                      type="button"
                      onClick={() =>
                        setForm({
                          ...form,
                          familles_interet: selected
                            ? form.familles_interet.filter((f) => f !== famille.nom)
                            : [...(form.familles_interet || []), famille.nom],
                        })
                      }
                      className={`rounded-full px-3 py-1.5 text-xs font-medium border transition ${
                        selected
                          ? "bg-blue-500/15 border-blue-400 text-blue-300"
                          : "bg-[#0B0F17] border-[#1E293B] text-slate-400 hover:border-slate-500 hover:text-slate-200"
                      }`}
                    >
                      {famille.nom}
                    </button>
                  );
                })}
              </div>

              {form.familles_interet?.length > 0 && (
                <p className="text-[11px] text-slate-500">
                  {form.familles_interet.length} famille{form.familles_interet.length > 1 ? "s" : ""} sélectionnée{form.familles_interet.length > 1 ? "s" : ""}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Ville</Label>
              <Input value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} className="bg-[#0B0F17] border-[#1E293B]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Téléphone</Label>
              <Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} className="bg-[#0B0F17] border-[#1E293B]" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-slate-400 text-xs">Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0B0F17] border-[#1E293B]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">Annuler</Button>
            <Button data-testid="client-save-button" onClick={create} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ActionLogDialog open={actionOpen} onOpenChange={setActionOpen} context={actionCtx} dossiers={detail?.devis} onSaved={refreshDetail} />
      <ProjectDetailSheet projectId={projectId} open={projectOpen} onOpenChange={setProjectOpen} onLogAction={logAction} refreshKey={refreshKey} />
    </div>
  );
}
